import {
  CUSTOMER_SUB_TYPE_LABELS,
  CUSTOMER_TYPE_LABELS,
  type Customer,
  type CustomerStatus,
  INVOICE_CADENCE_LABELS,
} from '@clockwise/clients/customers';
import type { Cadence, Sow, SowWithEngagementSummary } from '@clockwise/clients/sows';
import {
  ApiError,
  formatPhone,
  invoiceCadenceToSowFrequency,
  isInvoicingPausedOn,
} from '@clockwise/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, MoreHorizontal, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { AddressLines } from '@/components/custom/address-lines';
import { ScopedAuditPanel, useScopedAudit } from '@/components/custom/audit/scoped-audit';
import { BackLink } from '@/components/custom/back-link';
import { CustomFieldsViewer } from '@/components/custom/custom-fields-viewer';
import { CustomerActivitiesPanel } from '@/components/custom/customer-activities-panel';
import { BUDGET_PAGE_SIZE, CustomerBudgetsPanel } from '@/components/custom/customer-budgets-panel';
import { CustomerContactsPanel } from '@/components/custom/customer-contacts-panel';
import { CustomerExpensesPanel } from '@/components/custom/customer-expenses-panel';
import { CustomerFormSheet } from '@/components/custom/customer-form-sheet';
import { StatusBadge } from '@/components/custom/customer-status-badge';
import { CustomerSubsidiariesPanel } from '@/components/custom/customer-subsidiaries-panel';
import { CustomerTeamPanel } from '@/components/custom/customer-team-panel';
import { DetailRow, PreviousValues } from '@/components/custom/detail-list';
import {
  diffTarget,
  EntityVersionBar,
  previousValues,
  selectedVersion,
  useEntityVersions,
  withVersionValues,
} from '@/components/custom/entity-version-history';
import { NoAccess } from '@/components/custom/no-access';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { RequirePermission } from '@/components/custom/require-permission';
import { SowFormSheet } from '@/components/custom/sow-form-sheet';
import { PageShell } from '@/components/layout/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  customerBudgetsApi,
  customerContactsApi,
  customerExpensesApi,
  customersApi,
  customerTeamApi,
  departmentsApi,
  sowsApi,
} from '@/lib/api';
import { summarizeEngagements } from '@/lib/engagement-format';
import { isForbidden } from '@/lib/errors';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { useResolveCustomFieldLabel } from '@/lib/use-custom-field-definitions';
import { cn, localTodayIso } from '@/lib/utils';

const TAB_VALUES = [
  'details',
  'team',
  'contacts',
  'activities',
  'budgets',
  'invoicing',
  'engagements',
  'audit',
] as const;
type TabValue = (typeof TAB_VALUES)[number];

const detailSearch = z.object({
  // `.catch` so a bookmark carrying a pre-rename tab value (`?tab=sows`,
  // `?tab=billing`) lands on Details instead of throwing out of
  // validateSearch and crashing the page.
  tab: z.enum(TAB_VALUES).optional().catch(undefined),
  // Which version of the record the Details tab is showing. Absent = current.
  // In the URL so a specific version is linkable and survives a reload.
  version: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute('/_authenticated/customers/$id/')({
  validateSearch: detailSearch,
  component: CustomerDetailPage,
});

// Mirrors the first-page size used by CustomerTeamPanel so the tab-count query
// shares its cache entry.
const TEAM_PAGE_SIZE = 25;
// Mirrors CustomerExpensesPanel's page size for the same reason.
const CUSTOMER_EXPENSE_PAGE_SIZE = 25;
const DEFAULT_TAB: TabValue = 'details';

function CustomerDetailPage() {
  const { id } = Route.useParams();
  const { tab, version: requestedVersion } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const canViewTeam = useHasPermission('customer.team.view');
  const canViewBudgets = useHasPermission('customer.budget.view');
  const canViewCustomerExpenses = useHasPermission('customer.expense.view');
  const canViewContacts = useHasPermission('customer.contact.view');
  // Customer-specific activities require BOTH the customer and activity view
  // permissions (the server enforces the same intersection on the nested API).
  // Call both hooks unconditionally — `&&` would short-circuit the second
  // useHasPermission and change the hook count between renders.
  const canViewCustomer = useHasPermission('customer.view');
  const canViewActivityPerm = useHasPermission('activity.view');
  const canViewActivities = canViewCustomer && canViewActivityPerm;
  // The Audit tab reads /api/audit, which is gated by `audit.view`.
  const canViewAudit = useHasPermission('audit.view');
  // Internal-customer designation is admin-only; hide it from everyone else.
  const canSeeInternal = useHasPermission('customer.admin');
  // Same permission, different authority: `customer.admin` also carries the
  // historical amend (D10). Hiding the action rather than letting it 403.
  const canAmendHistory = canSeeInternal;
  const requestedTab: TabValue = tab ?? DEFAULT_TAB;
  // Don't land on a tab the caller can't see (e.g. a bookmarked ?tab=contacts
  // for a user without customer.contact.view) — fall back to details.
  const activeTab: TabValue =
    (requestedTab === 'contacts' && !canViewContacts) ||
    (requestedTab === 'team' && !canViewTeam) ||
    (requestedTab === 'activities' && !canViewActivities) ||
    (requestedTab === 'budgets' && !canViewBudgets) ||
    (requestedTab === 'invoicing' && !canViewCustomerExpenses) ||
    (requestedTab === 'audit' && !canViewAudit)
      ? DEFAULT_TAB
      : requestedTab;

  const detail = useQuery({
    queryKey: queryKeys.customers.detail(id),
    queryFn: () => customersApi.get(id),
  });

  const auditResolveLabel = useResolveCustomFieldLabel('customer');
  // Roll-up mode: the customer's own history PLUS its team / contacts / SOW
  // children (each entity_type still gated by the caller's `.view`).
  const audit = useScopedAudit({ scopeType: 'customers', scopeId: id, enabled: canViewAudit });

  // SOWs for this customer — every version so the tab can show active
  // and inactive (expired / scheduled) versions side by side.
  const sowsListQuery = useQuery({
    queryKey: [
      ...queryKeys.sows.list({
        customerId: id,
        sortField: 'effectiveFrom',
        sortDirection: 'asc',
        limit: 50,
        offset: 0,
      }),
      'all-versions',
    ],
    queryFn: () =>
      sowsApi.listAllVersions({
        customerId: id,
        sortField: 'effectiveFrom',
        sortDirection: 'asc',
        limit: 50,
        offset: 0,
      }),
  });

  // Counts shown on the Team / Contacts tabs. These mirror each panel's
  // default first-page fetch (in-effect today, no search) so the cache is
  // shared and no extra request is issued for the common case.
  const teamCountQuery = useQuery({
    queryKey: queryKeys.customerTeam.inEffect(id, { limit: TEAM_PAGE_SIZE, offset: 0 }),
    queryFn: () => customerTeamApi.listInEffect(id, { limit: TEAM_PAGE_SIZE, offset: 0 }),
    enabled: canViewTeam,
  });
  const contactsCountQuery = useQuery({
    queryKey: queryKeys.customerContacts.list(id),
    queryFn: () => customerContactsApi.list(id),
    enabled: canViewContacts,
  });
  // Pass-through lines in force TODAY — drives both the Invoicing tab's count
  // badge and the header chip. `asOf` defaults to today inside the panel too,
  // so this shares that cache entry and issues no extra request in the common
  // case. There is no "pass-through enabled" flag to read: a customer has
  // pass-through billing iff at least one line is in force, which is exactly
  // what this total is.
  const customerExpenseCountQuery = useQuery({
    queryKey: queryKeys.customerExpenses.list(id, {
      asOf: localTodayIso(),
      limit: CUSTOMER_EXPENSE_PAGE_SIZE,
      offset: 0,
    }),
    queryFn: () =>
      customerExpensesApi.list(id, {
        asOf: localTodayIso(),
        limit: CUSTOMER_EXPENSE_PAGE_SIZE,
        offset: 0,
      }),
    enabled: canViewCustomerExpenses,
  });
  const customerExpenseCount = customerExpenseCountQuery.data?.meta?.total ?? 0;

  /**
   * The Budgets tab's count.
   *
   * AS OF TODAY, not every budget ever written, because that is what the tab
   * opens on: a customer with four superseded agreements and one live one has
   * ONE budget in force, and a badge reading 5 over a table showing 1 would be
   * the tab disagreeing with its own heading. "Show past budgets" is how the
   * other four are reached, and it moves the table rather than this number.
   * Same rule as Invoicing above.
   *
   * Keyed exactly as `CustomerBudgetsPanel` keys its first page, so this is
   * that panel's cache entry and not a second request.
   */
  const budgetCountQuery = useQuery({
    queryKey: queryKeys.customerBudgets.list(id, {
      asOf: localTodayIso(),
      limit: BUDGET_PAGE_SIZE,
      offset: 0,
    }),
    queryFn: () =>
      customerBudgetsApi.list(id, {
        asOf: localTodayIso(),
        limit: BUDGET_PAGE_SIZE,
        offset: 0,
      }),
    enabled: canViewBudgets,
  });

  const customer = detail.data?.data ?? null;

  // Record history. `shownCustomer` is what the Details tab renders: the live
  // record, or a past version's values laid over it. The reference labels
  // (`salesRepName`, `parentName`) have to move with the ids they describe —
  // a historical salesRepId under today's name would name the wrong person.
  const versionsQuery = useEntityVersions('customer', id);
  const viewedVersion = selectedVersion(versionsQuery.data?.data, requestedVersion);
  const shownCustomer = customer
    ? withVersionValues(customer, viewedVersion, (labels) => ({
        salesRepName: labels.salesRepId ?? null,
        parentName: labels.parentCustomerId ?? null,
      }))
    : null;

  // "Show what changed" for the CURRENT version, off by default. Lives here
  // rather than inside the bar because `diffTarget` and `previousValues` below
  // must agree with the toggle — two copies of the flag could disagree and
  // strike through rows the banner says nothing about.
  const [showCurrentDiff, setShowCurrentDiff] = useState(false);

  const [sowSheetOpen, setSowSheetOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  function actionMutation(fn: () => Promise<unknown>, successMessage: string) {
    return async () => {
      try {
        await fn();
        toast.success(successMessage);
        queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Action failed';
        toast.error(msg);
      }
    };
  }

  const remove = actionMutation(() => customersApi.remove(id), 'Customer deactivated');
  const restore = actionMutation(() => customersApi.restore(id), 'Customer restored');

  // Backend returns 403 when this customer isn't in the caller's assigned set
  // (customer.view: assigned). Show the standard "Not Authorized" panel rather
  // than a destructive load error.
  if (detail.isError && isForbidden(detail.error)) {
    return (
      <PageShell>
        <NoAccess permission="customer.view" resource="this customer" />
      </PageShell>
    );
  }

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/customers">Back to customers</BackLink>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          {detail.isLoading ? (
            <>
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-72" />
            </>
          ) : detail.isError ? (
            <h1 className="text-2xl font-semibold text-destructive">
              {detail.error instanceof ApiError ? detail.error.message : 'Failed to load.'}
            </h1>
          ) : shownCustomer ? (
            <>
              {/* Name, status and the paused warning follow the SELECTED
                  version, matching the employee page — the header describes
                  the record as it stood then, and the banner below says which
                  "then" that is. The action controls above stay bound to the
                  live record. */}
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold">{shownCustomer.name}</h1>
                <StatusBadge status={shownCustomer.status} />
                {canSeeInternal && shownCustomer.isInternal ? (
                  <Badge variant="secondary">Internal</Badge>
                ) : null}
                {/* Only once the count query has SETTLED with a non-zero
                    total. Rendering it off a pending query would flash a chip
                    that then disappears; rendering it on error would claim
                    billed expenses we couldn't confirm. */}
                {canViewCustomerExpenses &&
                customerExpenseCountQuery.isSuccess &&
                customerExpenseCount > 0 ? (
                  <Badge variant="secondary">Expenses</Badge>
                ) : null}
              </div>
              {/* QB ID lives in the Details table; repeating it under the name
                  said the same thing twice. The paused warning stays — it is a
                  state worth seeing before you read anything else. */}
              <InvoicingPauseNotice customer={shownCustomer} />
            </>
          ) : (
            <h1 className="text-2xl font-semibold">Not found</h1>
          )}
        </div>

        {/* Hidden while a past version is on screen: every action here edits
            the CURRENT record, and offering them beside historical values
            invites someone to "fix" today's record back to an old value
            without realising which one they were looking at. */}
        {customer && !viewedVersion ? (
          <RequirePermission permission="customer.edit">
            <div className="flex items-center gap-2">
              <Button onClick={() => setEditOpen(true)}>
                <Pencil />
                Edit
              </Button>
              <StatusActionsMenu customer={customer} actions={{ remove, restore }} />
            </div>
          </RequirePermission>
        ) : null}
      </header>

      <PillTabs
        value={activeTab}
        onValueChange={(v) =>
          navigate({
            search: (s) => ({ ...s, tab: v === DEFAULT_TAB ? undefined : (v as TabValue) }),
            replace: true,
          })
        }
        className="rounded-xl border bg-card p-4"
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="details">Details</PillTabsTrigger>
          {canViewTeam ? (
            <PillTabsTrigger value="team">
              Team
              {teamCountQuery.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                  {teamCountQuery.data.meta.total}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
          {canViewContacts ? (
            <PillTabsTrigger value="contacts">
              Contacts
              {contactsCountQuery.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                  {contactsCountQuery.data.data.length}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
          {canViewActivities ? (
            <PillTabsTrigger value="activities">Activities</PillTabsTrigger>
          ) : null}
          {canViewBudgets ? (
            <PillTabsTrigger value="budgets">
              Budgets
              {budgetCountQuery.data ? (
                <span className="ml-1 text-xs text-muted-foreground">
                  {budgetCountQuery.data.meta.total}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
          {canViewCustomerExpenses ? (
            <PillTabsTrigger value="invoicing">
              Invoicing
              {customerExpenseCountQuery.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                  {customerExpenseCount}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
          <PillTabsTrigger value="engagements">
            Engagements
            {sowsListQuery.data ? (
              <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                {sowsListQuery.data.meta.total}
              </span>
            ) : null}
          </PillTabsTrigger>
          {canViewAudit ? (
            <PillTabsTrigger value="audit">
              Audit
              {audit.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                  {audit.data.meta.total}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
        </PillTabsList>

        <PillTabsContent value="details" className="space-y-3">
          <EntityVersionBar
            query={versionsQuery}
            selected={viewedVersion}
            onSelect={(v) => navigate({ search: (s) => ({ ...s, version: v }), replace: true })}
            onAmend={
              canAmendHistory
                ? (version) =>
                    navigate({ to: '/customers/$id/edit', params: { id }, search: { version } })
                : undefined
            }
            showCurrentDiff={showCurrentDiff}
            onShowCurrentDiffChange={setShowCurrentDiff}
          />
          <div className="rounded-md border">
            {detail.isLoading ? (
              <DetailsSkeleton />
            ) : shownCustomer ? (
              <PreviousValues
                values={previousValues(
                  diffTarget(versionsQuery.data?.data, viewedVersion, showCurrentDiff),
                )}
              >
                <DetailsTable customer={shownCustomer} />
              </PreviousValues>
            ) : null}
          </div>
          {customer ? <CustomerSubsidiariesPanel customerId={customer.id} /> : null}
          {customer ? (
            <CustomFieldsViewer object="customer" values={customer.customFields ?? null} />
          ) : null}
        </PillTabsContent>

        {canViewTeam ? (
          <PillTabsContent value="team" className="space-y-3">
            <CustomerTeamPanel customerId={id} />
          </PillTabsContent>
        ) : null}

        {canViewContacts ? (
          <PillTabsContent value="contacts" className="space-y-3">
            <CustomerContactsPanel customerId={id} />
          </PillTabsContent>
        ) : null}

        {canViewActivities ? (
          <PillTabsContent value="activities" className="space-y-3">
            <CustomerActivitiesPanel customerId={id} isInternal={customer?.isInternal ?? false} />
          </PillTabsContent>
        ) : null}

        {canViewBudgets ? (
          <PillTabsContent value="budgets" className="space-y-3">
            <CustomerBudgetsPanel customerId={id} />
          </PillTabsContent>
        ) : null}

        {canViewCustomerExpenses ? (
          <PillTabsContent value="invoicing" className="space-y-3">
            {/* Pass-through is the only pane this tab has today. The mock's
                `Invoices | Pass-through | Settings` segmented control lands
                when the other two exist — a segmented control with one segment
                is noise, and stub segments would be worse. */}
            <CustomerExpensesPanel customerId={id} />
          </PillTabsContent>
        ) : null}

        <PillTabsContent value="engagements" className="space-y-3">
          {customer ? (
            <RequirePermission permission="customer.sow.edit">
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setSowSheetOpen(true)}>
                  <Plus />
                  Add engagement
                </Button>
              </div>
            </RequirePermission>
          ) : null}
          {/* Waits on the customer too: a retainer naming no cadence bills on
              the customer's, and a card that printed a bare fee first and the
              cadence a moment later would flicker. */}
          {sowsListQuery.isLoading || detail.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : sowsListQuery.isError ? (
            <p className="p-4 text-sm text-destructive">
              {sowsListQuery.error instanceof ApiError
                ? sowsListQuery.error.message
                : 'Failed to load engagements.'}
            </p>
          ) : (
            <SowsList
              customerId={id}
              sows={sowsListQuery.data?.data ?? []}
              inheritedCadence={
                detail.isError ? 'unknown' : invoiceCadenceToSowFrequency(customer?.invoiceCadence)
              }
              onRowClick={(sowId) =>
                navigate({
                  to: '/customers/$id/engagements/$engagementId',
                  params: { id, engagementId: sowId },
                })
              }
            />
          )}
        </PillTabsContent>

        <PillTabsContent value="audit" className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Every state-changing operation, newest first.
          </p>
          <div className="rounded-md border">
            <ScopedAuditPanel query={audit} resolveLabel={auditResolveLabel} />
          </div>
        </PillTabsContent>
      </PillTabs>

      {customer ? (
        <SowFormSheet open={sowSheetOpen} onOpenChange={setSowSheetOpen} customerId={id} />
      ) : null}
      <CustomerFormSheet open={editOpen} onOpenChange={setEditOpen} customerId={id} />
    </PageShell>
  );
}

function StatusActionsMenu({
  customer,
  actions,
}: {
  customer: Customer;
  actions: {
    remove: () => Promise<void>;
    restore: () => Promise<void>;
  };
}) {
  const status: CustomerStatus = customer.status;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* `DropdownMenuTrigger asChild` clobbers this Button's own
            `data-slot="button"` with its own `data-slot="dropdown-menu-trigger"`
            (Radix Slot lets the outer wrapper's explicit props win), so the
            global `[data-slot='button']` pill-radius rule never matches this
            trigger. Set the radius directly instead of depending on that rule. */}
        <Button variant="outline" size="icon" className="rounded-full" aria-label="Status actions">
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* The only item in this menu, so it reads as a standalone action
            rather than a row in a list — a bordered pill/chip instead of
            the plain flush-hover row every other DropdownMenuItem uses
            (matching Approvals' "Search Approved" and Timesheets' "Settings"). */}
        {status !== 'inactive' ? (
          <DropdownMenuItem
            className="rounded-full border border-border bg-muted text-destructive focus:text-destructive"
            onClick={() => {
              if (confirm(`Deactivate "${customer.name}"? Sets status to inactive.`)) {
                actions.remove();
              }
            }}
          >
            Deactivate
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            className="rounded-full border border-border bg-muted"
            onClick={actions.restore}
          >
            Restore
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DetailsTable({ customer }: { customer: Customer }) {
  // Internal designation is admin-only — shown ahead of Created/Updated for those users.
  const canSeeInternal = useHasPermission('customer.admin');
  // [field, label, value] — `field` is the API field name, which is what
  // lets a row show the value the selected version replaced.
  const rows: [string, string, React.ReactNode][] = [
    ['name', 'Name', customer.name],
    ['legalName', 'Legal name', customer.legalName ?? <Muted>—</Muted>],
    ['qbId', 'QB ID', customer.qbId],
    ['phone', 'Phone', customer.phone ? formatPhone(customer.phone) : <Muted>—</Muted>],
    ['status', 'Status', <StatusBadge key="s" status={customer.status} />],
    ['departmentId', 'Department', <DepartmentName key="d" departmentId={customer.departmentId} />],
    ['parentCustomerId', 'Parent customer', <ParentCustomerLink key="p" customer={customer} />],
    ['startDate', 'Engagement Date', customer.startDate ?? <Muted>—</Muted>],
    ['creditTerm', 'Credit term', customer.creditTerm ?? <Muted>—</Muted>],
    ['defaultInvoiceType', 'Default invoice type', customer.defaultInvoiceType ?? <Muted>—</Muted>],
    ['corporateCalendar', 'Corporate calendar', customer.corporateCalendar ?? <Muted>—</Muted>],
    ['taxSpoc', 'Tax SPOC', customer.taxSpoc ?? <Muted>—</Muted>],
    [
      'customerType',
      'Customer type',
      customer.customerType ? CUSTOMER_TYPE_LABELS[customer.customerType] : <Muted>—</Muted>,
    ],
    [
      'customerSubType',
      'Customer sub-type',
      customer.customerSubType ? (
        CUSTOMER_SUB_TYPE_LABELS[customer.customerSubType]
      ) : (
        <Muted>—</Muted>
      ),
    ],
    [
      'invoiceCadence',
      'Invoice cadence',
      customer.invoiceCadence ? INVOICE_CADENCE_LABELS[customer.invoiceCadence] : <Muted>—</Muted>,
    ],
    [
      'salesRepId',
      'Sales rep',
      customer.salesRepId ? (
        // salesRepName is resolved server-side (a reference label on the
        // customer GET) — id set but name missing means the employee is gone.
        (customer.salesRepName ?? <Muted>Unknown employee</Muted>)
      ) : (
        <Muted>—</Muted>
      ),
    ],
    [
      'achAuthorized',
      'ACH authorization',
      customer.achAuthorized == null ? <Muted>—</Muted> : customer.achAuthorized ? 'Yes' : 'No',
    ],
    // Two rows, one per column, rather than one row phrasing the window as a
    // sentence: `field` is what the entity-version picker keys its
    // "this version changed it" mark on, so a combined row would leave an
    // end-date-only amendment unmarked on a screen of forty fields. The
    // human-readable phrasing lives in the banner under the customer's name,
    // which is the at-a-glance surface.
    [
      'invoicingPauseFrom',
      'Invoicing paused from',
      customer.invoicingPauseFrom ?? <Muted>Not paused</Muted>,
    ],
    [
      'invoicingPauseUntil',
      'Invoicing paused until',
      customer.invoicingPauseFrom ? (
        (customer.invoicingPauseUntil ?? <Muted>Indefinite</Muted>)
      ) : (
        <Muted>—</Muted>
      ),
    ],
    ['invoiceNotes', 'Invoice notes', customer.invoiceNotes ?? <Muted>—</Muted>],
    ['address', 'Physical address', <AddressBlock key="a" addr={customer.address} />],
    ['billingAddress', 'Billing address', <AddressBlock key="ba" addr={customer.billingAddress} />],
    [
      'billingContactId',
      'Billing contact',
      customer.billingContactId ? (
        <code className="font-mono text-xs">{customer.billingContactId}</code>
      ) : (
        <Muted>—</Muted>
      ),
    ],
    ...(canSeeInternal
      ? ([['isInternal', 'Internal', customer.isInternal ? 'Yes' : 'No']] as [
          string,
          string,
          React.ReactNode,
        ][])
      : []),
    ['createdAt', 'Created', new Date(customer.createdAt).toLocaleString()],
    ['updatedAt', 'Updated', new Date(customer.updatedAt).toLocaleString()],
  ];
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[160px_1fr_160px_1fr]">
      {rows.map(([field, label, value]) => (
        <DetailRow key={field} field={field} label={label}>
          {value}
        </DetailRow>
      ))}
    </dl>
  );
}

function DepartmentName({ departmentId }: { departmentId: string | null }) {
  const query = useQuery({
    queryKey: queryKeys.departments.detail(departmentId ?? ''),
    queryFn: () => departmentsApi.get(departmentId as string),
    enabled: Boolean(departmentId),
  });
  if (!departmentId) return <Muted>—</Muted>;
  if (query.isLoading) return <Skeleton className="h-4 w-24" />;
  return <>{query.data?.data.name ?? departmentId}</>;
}

/**
 * The parent company, linked to its own detail page.
 *
 * Unlike `DepartmentName` this does NOT fetch: `parentName` is projected onto
 * the customer GET server-side, precisely so an assigned-scope caller — who is
 * often not on the parent's team and would get a 403 from
 * `GET /api/customers/:id` — still sees the name. The link may therefore lead
 * to a page the user can't open; that's the same deal as any other cross-entity
 * link here, and better than hiding the relationship entirely.
 */
function ParentCustomerLink({ customer }: { customer: Customer }) {
  if (!customer.parentCustomerId) return <Muted>—</Muted>;
  // Id set but no name means the row is gone — impossible today (customers are
  // soft-deleted and the FK nulls the link on hard delete), so say so plainly
  // rather than rendering a link to nothing.
  if (!customer.parentName) return <Muted>Unknown customer</Muted>;
  // A LINK, deliberately — this was reviewed, changed to plain text, and
  // changed back. Don't "fix" it a third time without re-opening the decision.
  //
  // The parent's detail page is scope-gated, so a caller assigned only to this
  // subsidiary lands on Not Authorized. Nothing client-side can predict that:
  // `useHasPermission` is presence-only and its own docs say not to conflate it
  // with scope. The trade was made knowingly — the shortcut is worth more to
  // the many callers with unrestricted view than the occasional dead end costs
  // the few without, and Not Authorized is an accurate answer rather than a
  // broken link. The customers LIST renders the same value as plain text for a
  // different reason (its rows already navigate to the customer), so the two
  // surfaces differing is intentional, not drift.
  return (
    <Link
      to="/customers/$id"
      params={{ id: customer.parentCustomerId }}
      className="text-primary underline-offset-4 hover:underline"
    >
      {customer.parentName}
    </Link>
  );
}

/**
 * The other direction of the relationship, sitting directly under Parent so a
 * customer's place in the hierarchy reads in one place.
 *
 * A Details row rather than its own tab: there is nothing to manage here — the
 * link is set from the child's own form — so a tab would be a read-only view
 * that is empty for every customer that is not a parent company. The list
 * arrives on the customer response, so this costs no request of its own.
 *
 * Capped for the rare wide parent; the overflow count is stated rather than
 * silently dropped.
 */
function AddressBlock({ addr }: { addr: Customer['address'] }) {
  return (
    <AddressLines
      line1={addr?.street}
      locality={addr?.city}
      region={addr?.state}
      postalCode={addr?.postalCode}
      country={addr?.country}
    />
  );
}

function DetailsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_160px_1fr]">
      {Array.from({ length: 8 }).map((_, i) => (
        <DetailRow key={`skel-${i}`} label="">
          <Skeleton className="h-4 w-40" />
        </DetailRow>
      ))}
    </div>
  );
}

// SOW list — card-style rows (one per version) so active and inactive
// versions display together with their state at a glance.
function SowsList({
  customerId,
  sows,
  inheritedCadence,
  onRowClick,
}: {
  customerId: string;
  sows: SowWithEngagementSummary[];
  /** The customer's invoice cadence in the engagement vocabulary, which an
   *  engagement naming none bills on; `'unknown'` when the customer failed. */
  inheritedCadence: Cadence | null | 'unknown';
  onRowClick: (sowId: string) => void;
}) {
  if (sows.length === 0) {
    return (
      <div className="surface-card rounded-md border p-6 text-sm text-muted-foreground">
        No engagements yet. Click “Add engagement”.
      </div>
    );
  }
  // Newest version at the top.
  const ordered = [...sows].sort((a, b) =>
    a.effectiveFrom === b.effectiveFrom
      ? b.version - a.version
      : b.effectiveFrom.localeCompare(a.effectiveFrom),
  );
  return (
    <ul className="space-y-3">
      {ordered.map((s) => (
        <SowCard
          key={`${s.id}-${s.version}`}
          customerId={customerId}
          sow={s}
          inheritedCadence={inheritedCadence}
          onClick={() => onRowClick(s.id)}
        />
      ))}
    </ul>
  );
}

function SowCard({
  customerId,
  sow,
  inheritedCadence,
  onClick,
}: {
  customerId: string;
  sow: SowWithEngagementSummary;
  inheritedCadence: Cadence | null | 'unknown';
  onClick: () => void;
}) {
  const status = sowStatus(sow);
  // `?? []` only for an API that predates the summary (web and API roll out
  // separately): the card then shows "—" as it did before, instead of the
  // whole customer page throwing.
  const summary = summarizeEngagements(sow.engagements ?? [], inheritedCadence);
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full cursor-pointer rounded-md border bg-card p-4 text-left transition-colors hover:border-foreground/20 hover:bg-accent/30 focus-visible:outline-1 focus-visible:outline-ring"
      >
        <div className="flex items-start justify-between gap-3">
          <Link
            to="/customers/$id/sows/$sowId"
            params={{ id: customerId, sowId: sow.id }}
            className="font-medium underline-offset-2 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            v{sow.version} · {sow.name}
          </Link>
          <div className="flex items-center gap-2.5">
            {sow.documentNo ? (
              <code className="font-mono text-xs text-muted-foreground">{sow.documentNo}</code>
            ) : null}
            <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-muted-foreground">
              View
              <ChevronRight aria-hidden="true" />
            </span>
          </div>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-y-3 sm:grid-cols-4">
          <CardField label="Status">
            <SowStatusBadge status={status} />
          </CardField>
          <CardField label="Effective">
            <span className="text-sm">
              {sow.effectiveFrom}
              {sow.effectiveTo ? ` – ${sow.effectiveTo}` : ''}
            </span>
          </CardField>
          <CardField label="Model">{summary.model ?? <Muted>—</Muted>}</CardField>
          <CardField label="Fee">
            {summary.fee ? <span className="tabular-nums">{summary.fee}</span> : <Muted>—</Muted>}
          </CardField>
        </dl>
      </button>
    </li>
  );
}

function CardField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

type SowDerivedStatus = 'active' | 'scheduled' | 'expired';

function sowStatus(s: Sow): SowDerivedStatus {
  const today = localTodayIso();
  if (s.effectiveFrom > today) return 'scheduled';
  if (s.effectiveTo && s.effectiveTo <= today) return 'expired';
  return 'active';
}

// `active` matches the shared `LifecycleStatusBadge` pill exactly (activities
// catalog, rate cards, customer activities panel) — same date-range-derived
// Active/Scheduled/Expired pattern as the rate cards page, so "Active" reads
// identically wherever it appears.
const SOW_STATUS_STYLES: Record<SowDerivedStatus, string> = {
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  scheduled: 'bg-violet-100 text-violet-900 dark:bg-violet-900/30 dark:text-violet-200',
  expired: 'bg-muted text-muted-foreground',
};

function SowStatusBadge({ status }: { status: SowDerivedStatus }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        SOW_STATUS_STYLES[status],
      )}
    >
      {status}
    </span>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}

/**
 * The customer's invoicing pause, in words — or `null` when there is nothing
 * to say.
 *
 * A window, not a flag, so there are three states worth distinguishing and the
 * old "Yes/No" could only express one: a pause that has not started yet is a
 * commitment somebody made and should be visible BEFORE the invoice goes
 * missing, and one that has run its course is history rather than a warning.
 *
 * Dates are compared against the viewer's local today. The server judges a
 * pause against the period it is invoicing, which is the answer that decides
 * money; this is the answer that decides what a human is looking at right now.
 */
function describeInvoicingPause(c: {
  invoicingPauseFrom: string | null;
  invoicingPauseUntil: string | null;
}): string | null {
  const { invoicingPauseFrom: from, invoicingPauseUntil: until } = c;
  if (!from) return null;
  const today = localTodayIso();
  if (isInvoicingPausedOn(c, today)) {
    return until ? `Paused since ${from}, resuming ${until}` : `Paused since ${from}`;
  }
  if (today < from) {
    return until ? `Pauses ${from}, resuming ${until}` : `Pauses ${from}`;
  }
  // Past the window's end — `until` is non-null here, since an open-ended
  // pause that has started never expires.
  return `Paused ${from} – ${until}, resumed`;
}

/** The pause banner under the customer's name. Amber only while a pause is
 *  actually in force or still ahead; an expired one is stated in the details
 *  table and does not need to shout. */
function InvoicingPauseNotice({
  customer,
}: {
  customer: { invoicingPauseFrom: string | null; invoicingPauseUntil: string | null };
}) {
  const today = localTodayIso();
  const from = customer.invoicingPauseFrom;
  if (!from) return null;
  const active = isInvoicingPausedOn(customer, today);
  if (!active && today >= from) return null;
  const text = describeInvoicingPause(customer);
  if (!text) return null;
  return (
    <p className="text-sm text-amber-700 dark:text-amber-300">Invoicing {text.toLowerCase()}</p>
  );
}
